"use client";

import React from 'react';
import type { InlineEditProps } from './InlineEdit';
import { InlineEdit } from './InlineEdit';

const editingBehavior = {
  /**
   * Use 'button-with-double-click' when the container element is a button that
   * can be activated with a single click, and can be unmounted
   * with a double click.
   *
   * Examples: Finder in MacOS, when editing a file or folder name.
   * Asset card titles, where the card can be activated with a single click,
   * and unmount with a double click.
   */
  'button-with-double-click': {
    delayToEditVisible: 350,
    autoSelect: 'name',
    enabled: false,
  },

  /**
   * Use 'button' when the editable text container element is a button that can be
   * activated with a single click.
   *
   * Examples: A navigation item in a sidebar that when clicked, reveals
   * associated content to its right. When clicked after being activated,
   * the text becomes editable.
   */
  button: {
    delayToEditVisible: 0,
    autoSelect: 'name',
    enabled: false,
  },

  /**
   * Use 'static' when the editable text container isn't a button, or when you are explicitly
   * controlling the editable state of the component via `editing` props.
   *
   * Examples: A header, subtitle, or description field. A navigation button
   * that only becomes editable once a user sets it to editable via a contextual
   * menu.
   */

  static: {
    lineClamp: null as number | null,
    maxRowsWhileEditing: null as number | null,
    delayToEditVisible: 0,
    autoSelect: 'all',
    hoverBackgroundColor: 'primary',
    enableNewlines: true,
    cursor: 'text',
    enabled: true,
  },
} as const;

type TextEditProps = InlineEditProps & {
  /** Editing behaviors, which prescribe default sets of props. */
  mode?: keyof typeof editingBehavior;
};
/**
 * TextWithInlineEditing
 *
 * A plain text element that can be edited inline. It can be rendered
 * with the same colors and variants as the `Text` component.
 */

export function TextWithInlineEditing({
  mode = 'static',
  ...other
}: TextEditProps) {
  const defaultProps = editingBehavior[mode];
  // @ts-expect-error FIXME(strictNullChecks)
  return <InlineEdit {...defaultProps} {...other} />;
}
